import { Button, Form, Result, Skeleton, Typography } from "antd";

import axios from "axios";
import { message } from "components/Messages/Messages";
import { ContactPerson } from "containers/SingleJobPosting/interfaces";
import { Company } from "interfaces/companies";
import { Event, EventResponsibility, EventType, IsOpenType, Location } from "interfaces/events";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import CompanyCardAbout from "./Cards/CompanyCardAbout";
import CompanyCardDescription from "./Cards/CompanyCardDescription";
import CompanyCardGeneral from "./Cards/CompanyCardGeneral";
import CompanyCardResponsibleMembers from "./Cards/CompanyCardResponsibleMembers";
import CompanyCardStatus from "./Cards/CompanyCardStatus";
import CompanyCardTime from "./Cards/CompanyCardTime";
import CompanyCardWaitlist from "./Cards/CompanyCardWaitlist";

export type EventResponsibilityFormType = Pick<EventResponsibility, "bl_member" | "responsibility_type">;
export type EditEventFormType = {
  responsibilities: EventResponsibilityFormType[];
} & Event;

export const ResponsibilityType = {
  MAIN_RESPONSIBLE: 1,
  MEETING_RESPONSIBLE: 3,
} as const;

interface CreateOrEditEventProps {
  isNewEvent: boolean;
}

/**
 * Page for creating or editing an event.
 * If isNewEvent is true, the page is for creating a new event.
 * If isNewEvent is false, the page is for editing an existing event.
 */
const CreateOrEditEvent: React.FC<CreateOrEditEventProps> = ({ isNewEvent }) => {
  const { Title } = Typography;
  const { id } = useParams<{ id: string }>(); // Company-id if new event, event id if old event
  const [event, setEvent] = useState<Partial<Event>>();
  const [company, setCompany] = useState<Company>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [contactPersons, setContactPersons] = useState<ContactPerson[]>([]);
  const [responsibilities, setResponsibilities] = useState<EventResponsibilityFormType[]>([]);
  const [responsibilitiesWithId, setResponsibilitiesWithId] = useState<EventResponsibility[]>([]);
  const [fetchingEventOrCompany, setFetchingEventOrCompany] = useState<boolean>(true);

  const [processing, setProcessing] = useState(false);

  const browserHistory = useHistory();

  useEffect(() => {
    if (!isNewEvent) {
      axios
        .get<Event>(`/events/all-events/${id}/`)
        .then(response => {
          setEvent(response.data);
        })
        .finally(() => setFetchingEventOrCompany(false));
    }
  }, [id, isNewEvent]);

  useEffect(() => {
    if (company) return;
    isNewEvent
      ? axios
          .get<Company>(`/companies/${id}/`)
          .then(response => {
            setCompany(response.data);
          })
          .finally(() => setFetchingEventOrCompany(false))
      : setCompany(event?.company);
  }, [event, id, isNewEvent, company]);

  useEffect(() => {
    axios.get<Location[]>(`/events/locations/`).then(response => {
      setLocations(response.data);
    });
  }, []);

  useEffect(() => {
    axios.get<EventType[]>(`/events/event-types/`).then(response => {
      setEventTypes(response.data);
    });
  }, []);

  useEffect(() => {
    if (!company) return;
    axios.get<ContactPerson[]>(`/companies/${company.id}/contact-persons/`).then(response => {
      setContactPersons(response.data);
    });
  }, [company]);

  useEffect(() => {
    if (!event?.id) return;
    axios.get<EventResponsibility[]>(`/events/${event.id}/event-responsibility/`).then(response => {
      setResponsibilities(response.data as EventResponsibilityFormType[]);
      setResponsibilitiesWithId(response.data);
    });
  }, [event?.id]);

  useEffect(
    function initializeNewEvent() {
      if (isNewEvent) {
        setEvent({
          is_visible: 0,
          is_open: IsOpenType.NOT_OPENED,
          use_waiting_list: true,
          waiting_list_size: 15,
          overbooking: 0,
          location: undefined,
          company_contact_id: undefined,
          event_type: undefined,
          food: "",
          name: company?.name,
          start: undefined,
          open: undefined,
          close: undefined,
          description: "",
        });
      }
    },
    [isNewEvent, id, company]
  );

  const [form] = Form.useForm<EditEventFormType>();

  const onFinish = (values: EditEventFormType) => {
    const allValues = {
      ...event,
      ...values,
      company: isNewEvent ? Number(id) : event?.company?.id,
    };

    setProcessing(true);

    const eventRequest = isNewEvent
      ? axios.post(`/events/admin-events/`, allValues)
      : axios.put(`/events/admin-events/${id}/`, allValues);

    eventRequest
      .then(response => {
        const newResponsibilities = values.responsibilities;

        // Add responsibilities that aren't already in the database
        const addResponsibilities = newResponsibilities
          .filter(
            newResponsibility =>
              !responsibilities.some(
                responsibility => JSON.stringify(responsibility) === JSON.stringify(newResponsibility)
              )
          )
          .map(newResponsibility => axios.post(`/events/${response.data.id}/event-responsibility/`, newResponsibility));

        // Remove responsibilities that aren't in the new list
        const removeResponsibilities = responsibilitiesWithId
          .filter(
            responsibility =>
              !newResponsibilities.some(
                newResponsibility => JSON.stringify(newResponsibility) === JSON.stringify(responsibility)
              )
          )
          .map(responsibility =>
            axios.delete(`/events/${response.data.id}/event-responsibility/${responsibility.id}/`)
          );

        return Promise.all([...addResponsibilities, ...removeResponsibilities]).then(() => {
          message("success", `Arrangement ${isNewEvent ? "opprettet" : "oppdatert"}.`);
          browserHistory.push(`/admin/events/${response.data.id}/`);
        });
      })
      .catch(() => {
        message("error", `Kunne ikke ${isNewEvent ? "opprette" : "oppdatere"} arrangementet.`);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  if (fetchingEventOrCompany)
    return (
      <>
        <Title style={{ marginBottom: "16px" }}>{isNewEvent ? "Opprett arrangement" : "Oppdater arrangement"}</Title>
        <Skeleton.Button block active style={{ height: "150px" }} />
        <Skeleton.Button block active style={{ height: "150px", margin: "16px 0" }} />
        <Skeleton.Button block active style={{ height: "150px" }} />
        <Skeleton.Button block active style={{ height: "150px", margin: "16px 0" }} />
        <Skeleton.Button block active style={{ height: "150px" }} />
      </>
    );
  if (!event || !company)
    return (
      <Result
        title={isNewEvent ? "Fant ikke bedriften" : "Fant ikke arrangementet"}
        subTitle="Dobbeltsjekk URL eller prøv på nytt"
        status="404"
      />
    );

  const initialValues: Partial<EditEventFormType> = {
    is_visible: event.is_visible,
    is_open: event.is_open || IsOpenType.NOT_OPENED,
    use_waiting_list: event.use_waiting_list,
    waiting_list_size: event.waiting_list_size ?? 15,
    overbooking: event.overbooking ?? 0,
    location: event.location,
    company_contact_id: event.company_contact_id,
    event_type: event.event_type,
    food: event.food,
    name: event.name ?? company?.name,
    start: event.start,
    open: event.open,
    close: event.close,
    comments: event.comments,
    responsibilities: [],
  };

  return (
    <>
      <Title style={{ marginBottom: "16px" }}>{isNewEvent ? "Opprett arrangement" : "Oppdater arrangement"}</Title>

      <CompanyCardAbout company={company} />

      <Form form={form} onFinish={onFinish} initialValues={initialValues} layout="vertical" requiredMark={false}>
        <CompanyCardStatus form={form} />
        <CompanyCardWaitlist form={form} />
        <CompanyCardGeneral form={form} contactPersons={contactPersons} locations={locations} eventTypes={eventTypes} />
        <CompanyCardTime form={form} />
        <CompanyCardResponsibleMembers form={form} responsibilities={responsibilities} />
        <CompanyCardDescription form={form} />

        <Button
          loading={processing}
          htmlType="submit"
          type="primary"
          style={{ width: "100%", fontSize: "1.2em", height: "40px" }}
        >
          {isNewEvent ? "Opprett arrangement" : "Oppdater arrangement"}
        </Button>
      </Form>
    </>
  );
};

export const CreateEvent: React.FC = () => <CreateOrEditEvent isNewEvent />;
export const EditEvent: React.FC = () => <CreateOrEditEvent isNewEvent={false} />;
